import { safeLocalStorage, queryParams } from '@fluidx/utilities';
import { EcsSettingSource } from '../interfaces';
import { cacheVersion } from '../EcsClientConstants';
/**
 * Generates an ECS url based on a `EcsClientConfiguration`. This url is used to fetch settings from ECS.
 * @param ecsClientConfiguration The configuration of the `EcsClient`
 * @returns A string representing an ECS url.
 */
export function generateEcsFetchUrl(ecsClientConfiguration) {
    const queryParamsForUrl = new URLSearchParams();
    // parameter to scope the fetch to specific teams under the client in `ecsClientConfiguration.clientName`
    queryParamsForUrl.append('agents', ecsClientConfiguration.teamNames.join(','));
    // parameter to scope the fetch to specific teams under the other clients
    if (ecsClientConfiguration.guestAgents) {
        const guestAgents = {};
        for (let i = 0; i < ecsClientConfiguration.guestAgents.length; i += 1) {
            const clientName = ecsClientConfiguration.guestAgents[i].substring(ecsClientConfiguration.guestAgents[i].indexOf('(') + 1, ecsClientConfiguration.guestAgents[i].indexOf(')'));
            const ecsTeamNameWithoutClient = ecsClientConfiguration.guestAgents[i].substring(0, ecsClientConfiguration.guestAgents[i].indexOf('('));
            guestAgents[clientName] = guestAgents[clientName]
                ? `${guestAgents[clientName]},${ecsTeamNameWithoutClient}`
                : ecsTeamNameWithoutClient;
        }
        Object.entries(guestAgents).forEach(([key, value]) => {
            queryParamsForUrl.append('guestAgents', `${value}(${key})`);
        });
    }
    Object.entries(ecsClientConfiguration.filters).forEach(([key, value]) => {
        if (value !== undefined) {
            queryParamsForUrl.append(key, value);
        }
    });
    const queryString = queryParamsForUrl.toString();
    const url = `${ecsClientConfiguration.host}/config/v1/${ecsClientConfiguration.clientName}/${ecsClientConfiguration.clientVersion}${queryString !== '' ? `?${queryString}` : ''}`;
    return url;
}
/**
 * Gets the setting override from the query params.
 * @param settingLocator Object containing information about the setting.
 * @returns the setting and source when `settingName` is found, `undefined` otherwise.
 */
export function getSettingOverrideFromQueryParam(settingLocator) {
    const settingParamValue = queryParams.get(settingLocator.settingName);
    // Exception added when eslint rule was added, this should be revisited when modifying this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (settingParamValue !== null && settingParamValue !== undefined) {
        const settingValue = safeSettingValueParse(settingParamValue);
        // Checking for undefined value and not truthy because an empty string and 0 are valid setting values
        if (settingValue !== undefined) {
            const ecsSetting = {
                settingValue,
                ecsSettingSource: EcsSettingSource.QueryParamOverride
            };
            return ecsSetting;
        }
    }
    return undefined;
}
/**
 * Gets the setting override from the LocalStorage.
 * @param settingLocator Object containing information about the setting. Using the stringifiedLocator as a localStorage key.
 * @returns the setting and source when `settingName` is found, `undefined` otherwise.
 */
export function getSettingOverrideFromLocalStorage(settingLocator) {
    const localStorageValue = safeLocalStorage()?.getItem(settingLocator.settingName);
    if (localStorageValue !== null && localStorageValue !== undefined) {
        const settingValue = safeSettingValueParse(localStorageValue);
        // Checking for undefined value and not truthy because an empty string and 0 are valid setting values
        if (settingValue !== undefined) {
            const ecsSetting = {
                settingValue,
                ecsSettingSource: EcsSettingSource.LocalStorageOverride
            };
            return ecsSetting;
        }
    }
    return undefined;
}
/**
 * Helper function created for standardizing how to access a specific setting and also how to store it.
 * @param teamName name of the setting's team
 * @param settingName name of the setting
 * @returns a formatted string representing a setting from a specific team
 */
export function getSettingLocator(teamName, settingName) {
    return { settingName, teamName, stringifiedLocator: JSON.stringify({ teamName, settingName }) };
}
/**
 * Helper function used to validate that the expiration date and cache version are valid.
 * @param ecsResponse `EcsResponse` to validate
 * @returns `true` when the `EcsResponse` is valid, `false` otherwise
 */
export function ecsResponseIsStillValid(ecsResponse) {
    return ((ecsResponse.cacheVersion || 0) === cacheVersion &&
        ecsResponse.expires !== undefined &&
        ecsResponse.expires.valueOf() > Date.now());
}
function safeSettingValueParse(settingValue) {
    // Exception added when eslint rule was added, this should be revisited when modifying this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (settingValue !== null && settingValue !== undefined) {
        let value;
        try {
            value = JSON.parse(settingValue);
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any
      -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
        }
        catch (error) {
            // catching error to return undefined because JSON.parse could easily throw if string is not formatted as JSON
        }
        // this check is to assign the correct value whenever the settingValue the is not in Json Format
        const valueToReturn = value !== undefined ? value : settingValue;
        // We also allow 'on' and 'off' to represent true and false
        if (valueToReturn === 'on' || valueToReturn === 'off') {
            return valueToReturn === 'on';
        }
        return valueToReturn;
    }
    return undefined;
}
//# sourceMappingURL=EcsClientUtilities.js.map